import { useEffect, useState } from 'react';
import { Box, CircularProgress, TextField, Typography, useTheme } from '@mui/material';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { Redirect } from 'react-router';
import { ATSInviteToApplyTestIds } from 'data-testids/ATS';
import { Alert } from 'sharedComponents/Alert';
import { PrimaryButton } from 'sharedComponents/Buttons';
import { primaryButtonOverrideSx } from 'sharedComponents/Buttons/styles';
import { SelectAllListWithRHF } from 'sharedComponents/List/SelectAllListWithRHF';
import { getAxiosErrorMessageByStatusCode } from 'hooks/errorHandling/errorHandlingHooks';
import auth from 'utils/auth';
import { contactOptions } from '../data';
import {
  useQueryFetchCandidateForConnectATSIntegration,
  useQueryCheckIfCandidateHasApplication,
  useQuerySearchActiveRoles,
} from '../api/hiringRequestQueries';
import { JobComboBox } from '../CandidateImport/LandingPage/components/JobComboBox';
import { useInviteToApplyMutation } from './api/inviteToApplyMutations';
import { InviteToApplyExistingAppDisplay } from './InviteToApplyExistingAppDisplay';
import { InviteToApplySuccessPage } from './InviteToApplySuccessPage';
import { InviteToApplyFormState } from './types';
import {
  BoldTitle,
  CTAButtonContainer,
  ErrorText,
  Form,
  HelpText,
  Key,
  ModalContainer,
  Row,
  SubContainer,
} from '../styles/hiringRequestStyles';

const notFoundErrorMessage = 'Candidate not found. Please try again';

interface InviteToApplyFormProps {
  candidateId: number;
  onClose: () => void;
}

export const InviteToApplyForm: React.FC<InviteToApplyFormProps> = ({ candidateId, onClose }) => {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState('');
  const [debouncedInputValue, setDebouncedInputValue] = useState(inputValue);

  const {
    data: candidateInfo,
    error: candidateError,
    isError: isCandidateNotFoundError,
    isLoading: isCandidateLoading,
  } = useQueryFetchCandidateForConnectATSIntegration(candidateId);

  const methods = useForm<InviteToApplyFormState>({
    defaultValues: {
      role: null,
      contactMethods: [],
      internalNote: '',
    },
  });

  const { control } = methods;
  const selectedRole = useWatch({ control, name: 'role' });

  const districtRoleId = selectedRole?.district_role?.id ?? selectedRole?.id;

  const {
    data: existingAppData,
    isLoading: isExistingAppLoading,
    isError: isExistingAppError,
    error: existingAppError,
  } = useQueryCheckIfCandidateHasApplication(candidateId, districtRoleId, Boolean(districtRoleId));

  const {
    data: mutationData,
    mutate: submitInvite,
    isSuccess,
    isLoading: isSubmitting,
    isError: isSubmitError,
    error: submitError,
  } = useInviteToApplyMutation();

  const { data: roleOptions, isLoading: isRolesLoading } = useQuerySearchActiveRoles(
    debouncedInputValue,
    {
      enabled: debouncedInputValue.length > 0,
      keepPreviousData: true,
      staleTime: 60000,
      cacheTime: 120000,
    }
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedInputValue(inputValue);
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [inputValue]);

  const isRoleSearchLoading = isRolesLoading && Boolean(inputValue);

  if (!auth.hasConnectIntegrationModule()) {
    return <Redirect to="/principal/candidates" />;
  }

  const candidateErrorMessage = getAxiosErrorMessageByStatusCode(candidateError, {
    404: notFoundErrorMessage,
  });
  const hasInvalidCandidateId = isNaN(candidateId) || candidateId <= 0;

  if (hasInvalidCandidateId) {
    return <Alert type="error">{notFoundErrorMessage}</Alert>;
  }

  if (isCandidateNotFoundError) {
    return <Alert type="error">{candidateErrorMessage || notFoundErrorMessage}</Alert>;
  }

  if (isCandidateLoading) {
    return <CircularProgress data-testid={ATSInviteToApplyTestIds.FORM_LOADER} />;
  }

  const candidateName = candidateInfo?.name || '';
  const candidateEmail = candidateInfo?.email || '';

  const getLocationText = (): string => {
    if (selectedRole?.school?.name) {
      return selectedRole.school.name;
    }

    if (selectedRole?.schoolroles?.length > 0) {
      return selectedRole.schoolroles.map((sr) => sr?.name || sr?.school?.name).join(', ');
    }

    return 'District';
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const submitHandler = methods.handleSubmit((formState) => {
      const roleId = formState.role?.district_role?.id ?? formState.role?.id;
      submitInvite({
        candidate_id: candidateId,
        role_id: roleId,
        contact_methods: formState.contactMethods,
        internal_candidate_note_content: formState.internalNote,
      });
    });
    submitHandler(event);
  };

  const renderRoleHelpText = () => (
    <HelpText>
      Don&apos;t see the role you want? Contact your administrator for assistance.
    </HelpText>
  );

  if (isSuccess) {
    return (
      <InviteToApplySuccessPage
        candidateName={candidateName}
        candidateEmail={candidateEmail}
        roleTitle={selectedRole?.title}
        locationText={getLocationText()}
        contactMethods={mutationData?.contact_methods}
        onClose={onClose}
      />
    );
  }

  const renderPostRoleSelection = () => {
    if (!selectedRole) return null;

    if (isExistingAppLoading) {
      return <CircularProgress data-testid={ATSInviteToApplyTestIds.FORM_LOADER} />;
    }

    if (isExistingAppError) {
      const errorMessage =
        getAxiosErrorMessageByStatusCode(existingAppError) || 'Something went wrong';
      return (
        <Alert type="error" dataTestId={ATSInviteToApplyTestIds.FORM_ALERT}>
          {errorMessage}
        </Alert>
      );
    }

    if (existingAppData?.has_existing_app) {
      return <InviteToApplyExistingAppDisplay />;
    }

    return (
      <>
        <SubContainer>
          <Key>
            How have you communicated with this candidate about this role thus far? (optional)
          </Key>
          <HelpText sx={{ paddingBottom: theme.spacing(1) }}>Select all that apply</HelpText>
          <SelectAllListWithRHF
            dataTestId={ATSInviteToApplyTestIds.CONTACT_OPTIONS}
            formValue="contactMethods"
            itemKey="label"
            items={contactOptions}
            labelSx={{ marginBottom: 0 }}
            rules={{}}
            label={
              <Typography
                sx={{
                  '.MuiTypography-root': {
                    fontWeight: theme.typography.fontWeightMediumBold,
                  },
                }}
              >
                Select all
              </Typography>
            }
          />
        </SubContainer>

        <SubContainer data-testid={ATSInviteToApplyTestIds.NOTES_SECTION}>
          <Key sx={{ paddingBottom: theme.spacing(1) }}>
            Are there any notes you&apos;d like to include on the candidate&apos;s profile?
            (optional)
          </Key>
          <Controller
            name="internalNote"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextField
                size="small"
                onChange={onChange}
                value={value}
                fullWidth
                variant="outlined"
                multiline
                rows={6}
                placeholder={`E.g. I have spoken on the phone twice with ${candidateName}. I was impressed by their qualifications, especially their experience in Title I schools.`}
              />
            )}
          />
        </SubContainer>

        {isSubmitError && (
          <Alert type="error" dataTestId={ATSInviteToApplyTestIds.FORM_ALERT}>
            {getAxiosErrorMessageByStatusCode(submitError) || 'Something went wrong'}
          </Alert>
        )}

        <CTAButtonContainer>
          <PrimaryButton
            type="submit"
            disabled={isSubmitting || Boolean(methods.formState.errors.role)}
            isLoading={isSubmitting}
            dataTestId={ATSInviteToApplyTestIds.SEND_INVITE_BUTTON}
            sx={{
              ...primaryButtonOverrideSx(theme),
              width: '50%',
              boxShadow: 0,
              fontSize: theme.typography.body2.fontSize,
              letterSpacing: '0.2px',
            }}
          >
            Send Invite
          </PrimaryButton>
        </CTAButtonContainer>
      </>
    );
  };

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit}>
        <ModalContainer sx={{ width: 'inherit' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <BoldTitle variant="h1" data-testid={ATSInviteToApplyTestIds.TITLE}>
              Invite {candidateName} to Apply
            </BoldTitle>
          </Box>
          <SubContainer>
            <Row data-testid={ATSInviteToApplyTestIds.CANDIDATE_NAME}>
              <Key>Candidate:</Key> &nbsp;{candidateName}
            </Row>
            <Row data-testid={ATSInviteToApplyTestIds.CANDIDATE_EMAIL}>
              <Key>Candidate email:</Key> &nbsp; {candidateEmail}
            </Row>
          </SubContainer>

          <SubContainer data-testid={ATSInviteToApplyTestIds.ROLE_QUESTION}>
            <Key>What role do you want to invite this candidate to apply for?</Key>
            {renderRoleHelpText()}
            <Controller
              name="role"
              control={control}
              rules={{ required: 'Please select a role' }}
              render={({ field: { onChange } }) => (
                <JobComboBox
                  data-testid={ATSInviteToApplyTestIds.JOB_COMBOBOX}
                  onChange={onChange}
                  hasError={Boolean(methods.formState.errors.role)}
                  onInputChange={(text) => setInputValue(text)}
                  isLoading={isRoleSearchLoading}
                  options={roleOptions}
                  inputValue={inputValue}
                />
              )}
            />
            {methods.formState.errors.role && (
              <ErrorText>{methods.formState.errors.role?.message as string}</ErrorText>
            )}
          </SubContainer>

          {renderPostRoleSelection()}
        </ModalContainer>
      </Form>
    </FormProvider>
  );
};
